// Модальное окно привязки и создания грамматических правил к варианту упражнения.
// Поддерживает два режима: выбор из списка существующих правил и создание нового с AI-генерацией описания.

import { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
} from 'react-bootstrap';
import {
  getGrammarRulesByType,
  createGrammarRule,
  generateGrammarRule,
  linkGrammarRule,
} from '../../api/exerciseApi';

/**
 * Модальное окно для привязки / создания грамматического правила к варианту.
 *
 * Props:
 *   show             {bool}
 *   onHide           {() => void}
 *   exerciseId       {number}
 *   variantId        {number}
 *   typeId           {number}
 *   exerciseTypeName {string}
 *   variantQuestion  {string}
 *   variantKeyword   {string}
 *   currentRuleId    {number|null}
 *   onRuleSaved      {(rule) => void}
 */
export default function GrammarRuleModal({
  show,
  onHide,
  exerciseId,
  variantId,
  typeId,
  exerciseTypeName,
  variantQuestion,
  variantKeyword,
  currentRuleId,
  onRuleSaved,
}) {
  // Режимы: 'list' | 'create'
  const [mode, setMode] = useState('list');

  // ── List mode state ─────────────────────────────────────────────────────────
  const [rules, setRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selectedRuleId, setSelectedRuleId] = useState(currentRuleId ?? null);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [unlinking, setUnlinking] = useState(false);

  // ── Create mode state ───────────────────────────────────────────────────────
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // ── Загрузка правил при открытии ────────────────────────────────────────────
  useEffect(() => {
    if (!show) return;
    setMode('list');
    setSelectedRuleId(currentRuleId ?? null);
    setLinkError('');
    loadRules();
  }, [show]);

  const loadRules = async () => {
    setLoadingRules(true);
    setLoadError('');
    try {
      const { data } = await getGrammarRulesByType(typeId);
      setRules(data ?? []);
    } catch {
      setLoadError('Не удалось загрузить список правил');
    } finally {
      setLoadingRules(false);
    }
  };

  // ── Привязка правила ────────────────────────────────────────────────────────
  const handleLink = async () => {
    if (selectedRuleId === null) return;
    setLinking(true);
    setLinkError('');
    try {
      const { data: updatedVariant } = await linkGrammarRule(exerciseId, variantId, selectedRuleId);
      const linkedRule = rules.find((r) => r.id === selectedRuleId) ?? null;
      onRuleSaved(linkedRule ?? updatedVariant);
    } catch {
      setLinkError('Не удалось привязать правило. Попробуйте ещё раз.');
    } finally {
      setLinking(false);
    }
  };

  // ── Отвязка правила ─────────────────────────────────────────────────────────
  const handleUnlink = async () => {
    setUnlinking(true);
    setLinkError('');
    try {
      await linkGrammarRule(exerciseId, variantId, null);
      onRuleSaved(null);
    } catch {
      setLinkError('Не удалось отвязать правило. Попробуйте ещё раз.');
    } finally {
      setUnlinking(false);
    }
  };

  // ── Переключение в режим создания ──────────────────────────────────────────
  const handleGoCreate = () => {
    setNewTitle('');
    setNewDescription('');
    setGenerateError('');
    setSaveError('');
    setMode('create');
  };

  // ── Возврат в список ────────────────────────────────────────────────────────
  const handleBackToList = () => {
    const hasUnsaved = newDescription.trim() || newTitle.trim();
    if (hasUnsaved) {
      if (!window.confirm('Вы не сохранили правило. Вернуться без сохранения?')) return;
    }
    setMode('list');
  };

  // ── Закрытие модалки ────────────────────────────────────────────────────────
  const handleHide = () => {
    if (mode === 'create') {
      const hasUnsaved = newDescription.trim() || newTitle.trim();
      if (hasUnsaved) {
        if (!window.confirm('Вы не сохранили правило. Закрыть без сохранения?')) return;
      }
    }
    onHide();
  };

  // ── AI-генерация описания ────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateError('');
    try {
      const { data } = await generateGrammarRule({
        exercise_variant_id: variantId,
        keyword: variantKeyword,
        exercise_type_name: exerciseTypeName,
      });
      setNewDescription(data.generated_text ?? '');
    } catch {
      setGenerateError('Не удалось сгенерировать правило. Попробуйте ещё раз.');
    } finally {
      setGenerating(false);
    }
  };

  // ── Сохранение нового правила и привязка ────────────────────────────────────
  const handleSaveAndLink = async () => {
    if (!newTitle.trim()) {
      setSaveError('Введите заголовок правила');
      return;
    }
    setSaveError('');
    setSaving(true);
    try {
      const { data: created } = await createGrammarRule({
        title: newTitle.trim(),
        description: newDescription.trim(),
      });
      await linkGrammarRule(exerciseId, variantId, created.id);
      onRuleSaved(created);
    } catch {
      setSaveError('Не удалось сохранить правило. Попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  };

  // ── Заголовок модалки ────────────────────────────────────────────────────────
  const modalTitle = mode === 'create' ? 'Добавить правило' : 'Грамматическое правило';

  return (
    <Modal show={show} onHide={handleHide} size="lg" centered>
      <Modal.Header
        closeButton
        style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '1.25rem 1.5rem' }}
      >
        <Modal.Title style={{ fontSize: '1rem', fontWeight: 700, color: '#1e2a4a' }}>
          {modalTitle}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '1.5rem' }}>

        {/* ── Режим: список правил ──────────────────────────────────────────── */}
        {mode === 'list' && (
          <>
            {loadingRules && (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" variant="primary" size="sm" />
              </div>
            )}

            {loadError && (
              <Alert variant="danger" style={{ borderRadius: '0.5rem', fontSize: '0.875rem' }}>
                {loadError}
              </Alert>
            )}

            {linkError && (
              <Alert variant="danger" style={{ borderRadius: '0.5rem', fontSize: '0.875rem' }}>
                {linkError}
              </Alert>
            )}

            {!loadingRules && !loadError && rules.length === 0 && (
              <div className="text-center py-4" style={{ color: '#6c757d' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📋</div>
                <p className="mb-0" style={{ fontSize: '0.9rem' }}>
                  Правил ещё нет. Создайте первое.
                </p>
              </div>
            )}

            {!loadingRules && rules.length > 0 && (
              <div className="d-flex flex-column gap-2 mb-3">
                {rules.map((rule) => {
                  const isSelected = selectedRuleId === rule.id;
                  const isCurrent = currentRuleId === rule.id;
                  return (
                    <div
                      key={rule.id}
                      onClick={() => setSelectedRuleId(rule.id)}
                      style={{
                        background: isSelected ? 'rgba(79,110,247,0.07)' : '#f8f9fc',
                        border: `2px solid ${isSelected ? 'var(--bs-primary)' : '#e9ecef'}`,
                        borderRadius: '0.6rem',
                        padding: '0.875rem 1rem',
                        cursor: 'pointer',
                        transition: 'border-color 0.15s ease, background 0.15s ease',
                      }}
                    >
                      <div className="d-flex align-items-start gap-3">
                        <div
                          style={{
                            width: '1.125rem',
                            height: '1.125rem',
                            borderRadius: '50%',
                            border: isSelected ? '5px solid var(--bs-primary)' : '2px solid #ced4da',
                            background: isSelected ? 'var(--bs-primary)' : '#fff',
                            flexShrink: 0,
                            marginTop: '0.125rem',
                            transition: 'all 0.15s ease',
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            className="fw-semibold"
                            style={{ fontSize: '0.9rem', color: '#1e2a4a', marginBottom: '0.25rem' }}
                          >
                            {rule.title}
                            {isCurrent && (
                              <span
                                style={{
                                  marginLeft: '0.5rem',
                                  fontSize: '0.72rem',
                                  fontWeight: 500,
                                  color: 'var(--bs-primary)',
                                  background: 'rgba(79,110,247,0.1)',
                                  borderRadius: '0.3rem',
                                  padding: '0.1rem 0.4rem',
                                }}
                              >
                                Привязано
                              </span>
                            )}
                          </div>
                          {rule.description && (
                            <div
                              className="text-muted"
                              style={{ fontSize: '0.8rem', lineHeight: 1.5 }}
                            >
                              {rule.description.length > 100
                                ? rule.description.slice(0, 100) + '…'
                                : rule.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── Режим: создание правила ───────────────────────────────────────── */}
        {mode === 'create' && (
          <>
            {saveError && (
              <Alert variant="danger" style={{ borderRadius: '0.5rem', fontSize: '0.875rem' }}>
                {saveError}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold" style={{ fontSize: '0.875rem', color: '#1e2a4a' }}>
                Заголовок *
              </Form.Label>
              <Form.Control
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Например: Present Perfect — образование и употребление"
                style={{ borderRadius: '0.5rem' }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <Form.Label
                  className="fw-semibold mb-0"
                  style={{ fontSize: '0.875rem', color: '#1e2a4a' }}
                >
                  Описание
                </Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleGenerate}
                  disabled={generating}
                  style={{ borderRadius: '0.5rem', fontWeight: 500, fontSize: '0.8rem' }}
                >
                  {generating ? (
                    <><Spinner animation="border" size="sm" className="me-1" />Генерируем…</>
                  ) : '✨ Сгенерировать через AI'}
                </Button>
              </div>
              {generateError && (
                <Alert variant="warning" className="py-2 px-3 mb-2" style={{ borderRadius: '0.5rem', fontSize: '0.8rem' }}>
                  {generateError}
                </Alert>
              )}
              <Form.Control
                as="textarea"
                rows={6}
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Введите грамматическое правило или сгенерируйте через AI…"
                style={{ borderRadius: '0.5rem', resize: 'vertical' }}
              />
            </Form.Group>
          </>
        )}

      </Modal.Body>

      <Modal.Footer style={{ borderTop: '1px solid rgba(0,0,0,0.06)', padding: '1rem 1.5rem' }}>
        {mode === 'list' && (
          <>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleHide}
              style={{ borderRadius: '0.5rem', fontWeight: 500 }}
            >
              Закрыть
            </Button>

            {currentRuleId != null && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleUnlink}
                disabled={unlinking}
                style={{ borderRadius: '0.5rem', fontWeight: 500 }}
              >
                {unlinking ? (
                  <><Spinner animation="border" size="sm" className="me-1" />Отвязываем…</>
                ) : 'Отвязать'}
              </Button>
            )}

            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleGoCreate}
              style={{ borderRadius: '0.5rem', fontWeight: 500 }}
            >
              + Добавить правило
            </Button>

            <Button
              variant="primary"
              size="sm"
              onClick={handleLink}
              disabled={selectedRuleId === null || linking}
              style={{ borderRadius: '0.5rem', fontWeight: 600 }}
            >
              {linking ? (
                <><Spinner animation="border" size="sm" className="me-1" />Привязываем…</>
              ) : 'Привязать'}
            </Button>
          </>
        )}

        {mode === 'create' && (
          <>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleBackToList}
              style={{ borderRadius: '0.5rem', fontWeight: 500 }}
            >
              ← Назад
            </Button>

            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveAndLink}
              disabled={saving}
              style={{ borderRadius: '0.5rem', fontWeight: 600 }}
            >
              {saving ? (
                <><Spinner animation="border" size="sm" className="me-1" />Сохраняем…</>
              ) : 'Сохранить и привязать'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
